import { useEffect } from 'react';
import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';
import type { EntityType } from 'lib/entities';
import { api } from 'lib/request/api';
import type {
  EntityActivitySource,
  EntityLibraryAnalyticsResponse,
} from 'types/api-responses';

/** Query-key prefix for the entity-library analytics read. */
export const ENTITY_LIBRARY_ANALYTICS_QUERY_KEY =
  'entities:library-analytics' as const;

interface EntityLibraryAnalyticsParams {
  startDate: string;
  endDate: string;
  entityTypes: EntityType[];
  sources: EntityActivitySource[];
  /** Skip the read (panel closed or the selected window is invalid). */
  enabled: boolean;
}

interface EntityLibraryAnalytics {
  data: EntityLibraryAnalyticsResponse | undefined;
  loading: boolean;
  error: Error | null;
  /** Re-run the query now (bypasses the cache) — backs the Refresh button. */
  refresh: () => void;
}

/**
 * The analytics read for a window and filters: its key and fetcher together.
 * No `staleTime` on purpose — every mount/filter change should hit the server.
 * @param {EntityLibraryAnalyticsParams} params - The window, filters, and whether to read
 * @return The queryOptions for that read
 */
export const entityLibraryAnalyticsQueryOptions = ({
  startDate,
  endDate,
  entityTypes,
  sources,
  enabled,
}: EntityLibraryAnalyticsParams) =>
  queryOptions({
    // Sorted so the same selection in a different click order shares one cache entry.
    queryKey: [
      ENTITY_LIBRARY_ANALYTICS_QUERY_KEY,
      startDate,
      endDate,
      [...entityTypes].sort(),
      [...sources].sort(),
    ],
    queryFn: ({ signal }) =>
      // `wrap_parameters :query` nests the body.
      api
        .post('queries/entities/analytics', {
          json: {
            query: {
              startDate,
              endDate,
              entityTypes: entityTypes.length ? entityTypes : undefined,
              sources: sources.length ? sources : undefined,
            },
          },
          signal,
        })
        .json<EntityLibraryAnalyticsResponse>(),
    enabled,
    // Keeps the panel content mounted (no reflow) while a filter change refetches.
    placeholderData: keepPreviousData,
  });

/**
 * Real-time entity-library analytics for the side panel.
 * @param {EntityLibraryAnalyticsParams} params - The window, filters, and whether to read
 * @return {EntityLibraryAnalytics} Payload plus loading/error state and a
 *   cache-bypassing `refresh`.
 */
export function useEntityLibraryAnalytics(
  params: EntityLibraryAnalyticsParams
): EntityLibraryAnalytics {
  const { data, isFetching, error, refetch } = useQuery(
    entityLibraryAnalyticsQueryOptions(params)
  );

  return {
    data,
    loading: isFetching,
    error: error ?? null,
    refresh: () => {
      void refetch();
    },
  };
}

/**
 * Clears the room a slide-in SidePanel pushed the page by, when the panel
 * unmounts. SidePanel clears it only on open→false, so a panel that unmounts
 * open — its link or any route change leaving the page — would leave the page
 * squeezed.
 * @param {string} selector - The SidePanel's `selectorPageContent`
 */
export const useClearPagePushOnUnmount = (selector: string) => {
  useEffect(
    () => () => {
      const pageContent = document.querySelector(selector);
      if (pageContent instanceof HTMLElement) {
        pageContent.style.marginInlineEnd = '';
        pageContent.style.marginInlineStart = '';
        pageContent.style.inlineSize = '';
      }
    },
    [selector]
  );
};
